import { ResponseCode } from '@/common/request/types';
import { Role } from '@/modules/auth/types';
import { roleService } from '@/services/role';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const useAuthStore = defineStore(
    'store.auth',
    () => {
        const token = ref<string | null>(null);
        const trustToken = ref<string | null>(null);
        const sysRoles = ref<Role[]>([]);
        const appRoles = ref<Role[]>([]);

        const hasSystemRole = (role: Role): boolean => sysRoles.value.includes(role);
        const hasAppRole = (role: Role): boolean => appRoles.value.includes(role);
        const isAuthenticated = computed(() => Boolean(token.value));
        const isAdmin = computed(() => hasSystemRole(Role.ADMIN));
        const isManager = computed(() => hasSystemRole(Role.MANAGER));
        const isSuperUser = computed(() => hasSystemRole(Role.SUPER_USER));
        const isAppAdmin = computed(() => hasAppRole(Role.ADMIN));
        const isAppManager = computed(() => hasAppRole(Role.MANAGER));

        function setToken(newToken: string | null): void {
            token.value = newToken;
        }

        function setTrustToken(newTrustToken: string | null): void {
            trustToken.value = newTrustToken;
        }

        async function loadSystemRoles(): Promise<void> {
            const response = await roleService.system.get();
            if (response.code === ResponseCode.Ok) {
                sysRoles.value = normalizeRoles(response.data ?? []);
            }
        }

        async function loadAppRoles(): Promise<void> {
            const response = await roleService.app.get();
            if (response.code === ResponseCode.Ok) {
                appRoles.value = normalizeRoles(response.data ?? []);
            }
        }

        const loadRoles = async (): Promise<void> => {
            await Promise.all([loadSystemRoles(), loadAppRoles()]);
        };

        const logout = (): void => {
            token.value = null;
            sysRoles.value = [];
            appRoles.value = [];
        };

        return {
            token,
            trustToken,
            sysRoles,
            appRoles,

            isAuthenticated,
            isAppAdmin,
            isAppManager,
            isAdmin,
            isManager,
            isSuperUser,

            setToken,
            setTrustToken,

            loadRoles,
            logout
        };
    },
    {
        persist: true
    }
);

function normalizeRoles(roles: Array<string>) {
    const validRoles = new Set(Object.values(Role));
    return roles.filter(role => validRoles.has(role as Role)) as Role[];
}
